import {
  Edit,
  Ellipsis,
  IconButton,
  Menu,
  PaletteCircle,
  Reply,
  Trash,
} from '@sparemin/blockhead';
import React, { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { ClosedCaptioning, Scissors } from 'components/icons';
import useRunInDeferredTab from 'hooks/useRunInDeferredTab';
import { pushModal } from 'redux/modules/modal';
import { Dispatch } from 'redux/types';
import { ClipSuggestionDislikeReason } from 'types';
import { DEFAULT_PREPARING_CLIP_TAB_MESSAGE } from '../../../constants';
import { useClipSelect } from '../../ClipSelectContext';
import { useClipSelectNavigation } from '../../ClipSelectNavigationContext';
import { useClipSelectTracking } from '../../ClipSelectTrackingContext';
import { PreviewCardMenuOption } from './types';
import { block } from './utils';

export type AudioClipSuggestionPreviewCardMenuProps = {
  clipId: number;
  clipName: string;
  isDisabled?: boolean;
  onClickCaptions: () => void;
};

const AudioClipSuggestionPreviewCardMenu: React.FC<AudioClipSuggestionPreviewCardMenuProps> = ({
  clipId,
  clipName,
  isDisabled,
  onClickCaptions,
}) => {
  const {
    exportClip,
    onEditTemplate,
    onDislikeClip,
    prepareAdvancedEditorUrl,
    prepareShareUrl,
    pauseAll,
    suggestion,
    updateSuggestionProjectName,
  } = useClipSelect({
    clipId,
  });
  const {
    onOpenInAdvancedEditor: trackOnOpenInAdvancedEditor,
  } = useClipSelectTracking();
  const runInTab = useRunInDeferredTab(DEFAULT_PREPARING_CLIP_TAB_MESSAGE);

  const [state] = useClipSelectNavigation();

  // when the user clicks the share button, the clip might have to export first.
  // this typically takes a while and the user might have moved on to other tasks
  // in the meantime.
  //
  // when the user shares, we record their intent, but as conditions change, the
  // intent might change to prevent unexpected side effects.  for example, we
  // might not want to open the clip in a new tab when it's done processing if the
  // user has navigated away
  const intent = useRef<'share' | undefined>();
  const isBrowse = state.matches('select');

  const dispatch = useDispatch<Dispatch>();

  if (!isBrowse && intent.current === 'share') {
    intent.current = undefined;
  }

  const handleRename = useCallback(async () => {
    const projectName = await dispatch(
      pushModal({
        name: 'RenameClip',
        params: { projectId: suggestion?.projectUuid, clipName },
      }),
    );

    if (projectName) {
      updateSuggestionProjectName(projectName);
    }
  }, [
    clipName,
    dispatch,
    suggestion?.projectUuid,
    updateSuggestionProjectName,
  ]);

  const handleSubmit = useCallback(() => {
    intent.current = 'share';
    pauseAll();
    runInTab(
      () =>
        new Promise<string | undefined>((resolve, reject) => {
          // The clip exports first; only point the tab at the share url if the
          // user's intent is still 'share' once export finishes
          exportClip(async () => {
            try {
              resolve(
                intent.current === 'share'
                  ? await prepareShareUrl()
                  : undefined,
              );
            } catch (err) {
              reject(err);
            }
          })
            // exportClip can resolve without running the callback (e.g. the
            // clip is already exporting), so settle here too - otherwise the
            // promise never resolves and the blank tab is orphaned. resolve is
            // idempotent, so this is a no-op when the callback already ran.
            .then(() => resolve(undefined))
            .catch(reject);
        }),
    );
  }, [exportClip, prepareShareUrl, pauseAll, runInTab]);

  const handleEdit = useCallback((): void => {
    trackOnOpenInAdvancedEditor();
    runInTab(prepareAdvancedEditorUrl);
  }, [prepareAdvancedEditorUrl, runInTab, trackOnOpenInAdvancedEditor]);

  const handleOptionClick = useCallback(
    (option: PreviewCardMenuOption) => {
      switch (option) {
        case 'captions':
          onClickCaptions();
          break;

        case 'design':
          onEditTemplate();
          break;

        case 'edit':
          handleEdit();
          break;

        case 'rename':
          handleRename();
          break;

        case 'remove':
          onDislikeClip(ClipSuggestionDislikeReason.NotInteresting, suggestion);
          break;

        case 'share':
          handleSubmit();
          break;

        default:
          break;
      }
    },
    [
      handleEdit,
      handleRename,
      handleSubmit,
      onClickCaptions,
      onEditTemplate,
      onDislikeClip,
      suggestion,
    ],
  );

  return (
    <Menu
      placement="bottom end"
      density="comfortable"
      offset={10}
      button={
        <IconButton
          size="large"
          isDisabled={isDisabled}
          className={block('menu-trigger-button')}
        >
          <Ellipsis className={block('menu-trigger-button-icon')} />
        </IconButton>
      }
      onAction={handleOptionClick}
      items={[
        { key: 'captions', icon: <ClosedCaptioning />, label: 'Captions' },
        { key: 'rename', icon: <Edit />, label: 'Rename' },
        { key: 'design', icon: <PaletteCircle />, label: 'Design' },
        { key: 'edit', icon: <Scissors height={14} />, label: 'Edit' },
        { key: 'share', icon: <Reply />, label: 'Share' },
        { key: 'remove', icon: <Trash />, label: 'Remove' },
      ]}
    >
      {item => (
        <Menu.Item key={item.key} icon={item.icon}>
          {item.label}
        </Menu.Item>
      )}
    </Menu>
  );
};

export default AudioClipSuggestionPreviewCardMenu;
